import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { profileData } from '@/mocks/profile';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于佐佐木明希粉丝站',
  description: '佐佐木明希官方粉丝资料站，提供艺人档案、作品全览与最新动态。',
  url: 'https://akiupdate.github.io/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('idle');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复！');
      setSubmitting(false);
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9u5a0tca23r0tr1ar3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('发送失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '发送失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <SEOHead
        title="关于我们 - 佐佐木明希粉丝站 | 品牌介绍、口碑评价与联系方式"
        description="了解佐佐木明希粉丝站的品牌故事、口碑评价和联系方式。我们致力于为粉丝提供最全面的佐佐木明希个人档案、作品全览与最新动态。"
        keywords="佐佐木明希,关于我们,佐佐木明希粉丝站,联系我们,佐佐木明希资料站"
        canonicalUrl="https://akiupdate.github.io/about"
      />
      <div className="pt-20">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Page Header */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8" aria-label="关于我们标题">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            关于我们
          </h2>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
            佐佐木明希官方粉丝资料站，致力于为广大粉丝提供最全面、最及时的佐佐木明希相关资讯。
          </p>
        </section>

        {/* Brand Introduction */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8" aria-label="品牌介绍">
          <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium mb-4">
              品牌介绍
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
              我们的使命
            </h3>
            <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
              <p>
                佐佐木明希粉丝站成立于2020年，是由一群热爱佐佐木明希的资深粉丝共同创建的非官方资料平台。我们怀着对佐佐木明希的喜爱与敬意，致力于打造一个全面、专业、持续更新的线上资料库。
              </p>
              <p>
                我们的使命是让每一位关注佐佐木明希的粉丝，都能够在这里轻松找到她的个人档案、出道经历、代表作品以及最新动态。无论你是刚刚认识她的新粉丝，还是陪伴她多年的老粉丝，这里都是你了解她的最佳窗口。
              </p>
              <p>
                在过去几年中，我们持续收录了超过<strong>350部作品</strong>的详细信息，发布了<strong>数百篇</strong>原创专题文章和新闻报道，积累了<strong>超过120万</strong>忠实用户。我们相信，真实、及时、全面的信息才是对偶像最好的支持。
              </p>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8" aria-label="口碑评价">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium mb-4">
            口碑评价
          </span>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            粉丝心声
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: '东京粉丝 T.K.',
                text: '这个网站是我了解佐佐木明希最新动态的首选平台。作品信息非常全面，更新也很及时，强烈推荐给每一位粉丝！',
                date: '2025年1月',
              },
              {
                name: '大阪粉丝 M.Y.',
                text: '从2018年开始使用这个粉丝站，见证了它一步步的成长。现在的界面非常美观，功能也很完善，感谢运营团队的付出。',
                date: '2024年12月',
              },
              {
                name: '海外粉丝 L.W.',
                text: '作为一个海外粉丝，能找到这样一个全面的中文资料站真的非常开心。作品分类清晰，文章质量也很高，让我更深入地了解了佐佐木明希。',
                date: '2024年11月',
              },
            ].map((review) => (
              <div key={review.name} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-900 text-sm font-medium">{review.name}</span>
                  <span className="text-foreground-400 text-xs">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Stats Bar */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8" aria-label="数据展示">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '350+', label: '收录作品' },
              { value: '120万+', label: '粉丝关注' },
              { value: '5年', label: '持续运营' },
              { value: '99.8%', label: '信息准确率' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.value}</div>
                <div className="text-foreground-400 text-xs font-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 mb-10" aria-label="联系我们">
          <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium mb-4">
              联系我们
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              与我们取得联系
            </h3>
            <p className="text-foreground-500 text-sm mb-8">
              如有任何问题、建议或合作意向，欢迎通过以下表单联系我们。我们会在48小时内回复。
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                />
                <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <div className="honeypot-field" aria-hidden="true">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus !== 'idle' && (
                <div
                  className={`p-4 rounded-xl text-sm ${
                    formStatus === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    发送中...
                  </>
                ) : (
                  '发送留言'
                )}
              </button>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}